import { useState } from "react"
import { usePage } from "@inertiajs/react"

import { Card, CardContent } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { LocationSectionScreen } from "@/features/staff/location-section-screen"
import { t } from "@/lib/i18n"
import { patchInline, toastInlineUpdateError, toastInlineUpdateSuccess } from "@/lib/inline-update"

const tSection = (key: string) =>
  t(`ui.react.pages.staff.location.manage.location.sections.${key}`)

/**
 * Only the features the platform granted this location. One the restaurant was
 * never given is absent rather than disabled — a greyed-out switch invites a
 * support ticket about a product they cannot buy from this screen anyway.
 */
type LocationFeature = {
  key: string
  label: string
  description: string
  enabled: boolean
  /**
   * Set when the switch is on and the feature still cannot work, so the row can
   * say why instead of leaving the restaurant to discover it from missing orders.
   * Only pickup has one today: its menu lives at a slug, so without one there is
   * no address for a diner to reach.
   */
  blockedReason?: string | null
}

/**
 * The granted features, already split into the catalog's sections and already
 * ordered. Grouping client-side would mean keeping a copy of the section order
 * in TypeScript, and the next feature added to `config/location_features.yml`
 * would land wherever that stale copy happened to list it — including nowhere.
 */
type FeatureSection = {
  group: string
  label: string
  features: LocationFeature[]
}

type Props = { features: FeatureSection[] }

function FeatureSwitches({ sections }: { sections: FeatureSection[] }) {
  // The switch reflects the server, not the tap: it moves when the reloaded props
  // say it moved. Disabling the row in flight keeps a double-tap from racing.
  const [pendingKey, setPendingKey] = useState<string | null>(null)

  const toggle = (key: string, enabled: boolean) => {
    setPendingKey(key)
    patchInline(`/manage/location/features/${key}`, { enabled })
      .then((result) => toastInlineUpdateSuccess(result))
      .catch((error: unknown) => toastInlineUpdateError(error))
      .finally(() => setPendingKey(null))
  }

  if (sections.length === 0) {
    return <p className="text-sm text-muted-foreground">{tSection("features_empty")}</p>
  }

  // A lone heading over the only section names nothing the card title has not
  // already said. The padding needs no such guard: `first:pt-0 last:pb-0`
  // already collapses to nothing when the only section is both.
  const showHeadings = sections.length > 1

  return (
    <div className="divide-y divide-border" data-testid="location-features">
      {sections.map((section) => (
        <section key={section.group} className="py-6 first:pt-0 last:pb-0">
          {showHeadings && (
            <h3
              className="mb-4 truncate text-sm font-semibold tracking-tight"
              data-testid={`location-feature-group-${section.group}`}
            >
              {section.label}
            </h3>
          )}

          <div className="space-y-4">
            {section.features.map((feature) => (
              <div key={feature.key} className="flex items-start justify-between gap-4">
                <div className="min-w-0 space-y-1">
                  <Label htmlFor={`feature-${feature.key}`} className="text-sm font-medium">
                    {feature.label}
                  </Label>
                  <p className="text-xs text-muted-foreground">{feature.description}</p>
                  {/*
                    The switch says on and the feature is not working. Pickup needs an
                    address — its menu lives at a slug — so without one the restaurant
                    would receive nothing and never learn why. Warning rather than
                    destructive: nothing is broken, something is missing.
                  */}
                  {feature.blockedReason === "missing_slug" && (
                    <p
                      className="text-xs text-warning"
                      data-testid={`feature-${feature.key}-blocked`}
                    >
                      {tSection("feature_blocked_missing_slug")}
                    </p>
                  )}
                </div>
                <Switch
                  id={`feature-${feature.key}`}
                  checked={feature.enabled}
                  disabled={pendingKey === feature.key}
                  onCheckedChange={(next) => toggle(feature.key, next)}
                  data-testid={`location-feature-${feature.key}-switch`}
                />
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}


export default function StaffLocationManageLocationFeatures() {
  const { features } = usePage<Props>().props

  return (
    <LocationSectionScreen
      title={tSection("features_title")}
      description={tSection("features_description")}
    >
      <Card>
        <CardContent>
          <FeatureSwitches sections={features} />
        </CardContent>
      </Card>
    </LocationSectionScreen>
  )
}
